import { useState, useEffect, useMemo } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Divider,
	Chip,
	TableSortLabel,
	InputAdornment,
	IconButton,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { provinceApi, schoolApi } from '../../services';
import { useErrorHandler } from '../../lib/errorHandler';
import { ProvinceSchema } from '../../services/provinceApi';
import { TruongTHPTSchema } from '../../services/schoolApi';
import { z } from 'zod';
import SchoolDialog from './SchoolDialog';
import { useAppSelector } from '../../store/hooks';
import { DIALOG_SETTINGS, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX } from '../../utils/responsive';
import { useResponsiveDialog, useEntityDialog } from './hooks';
import { DialogFormActions, StatisticsPanel } from './base';
import { type SchoolWithYearData } from '../../schemas/statsSchema';

type Province = z.infer<typeof ProvinceSchema>;
type HighSchool = z.infer<typeof TruongTHPTSchema>;

interface ProvinceDialogProps {
	open: boolean;
	onClose: () => void;
	province?: Province | null;
	mode: 'create' | 'edit' | 'view';
	year?: number;
	top149Only?: boolean;
	merged?: boolean;
}

export default function ProvinceDialog({ open, onClose, province, mode, year, top149Only = false, merged = false }: ProvinceDialogProps) {
	const { showError } = useErrorHandler();

	// Responsive and mode helpers
	const { isMobile, isReadOnly, isEditMode, isViewMode } = useResponsiveDialog({ mode });
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	// `maTinhMoi` rỗng nghĩa là "không sáp nhập"; API nhận null cho trạng thái đó.
	const [formData, setFormData] = useState({
		maTinh: '',
		tenTinh: '',
		maTinhMoi: '',
	});

	// Entity mutations
	const { isLoading, handleCreate, handleUpdate } = useEntityDialog<
		{ maTinh: string; tenTinh: string },
		{ tenTinh: string; maTinhMoi: string | null }
	>({
		entityName: 'tỉnh/thành phố',
		queryKeys: [['provinces']],
		createFn: provinceApi.createProvince,
		updateFn: (id, data) => provinceApi.updateProvince(id, data),
		onSuccess: onClose,
	});

	const [schoolSortOrder, setSchoolSortOrder] = useState<'asc' | 'desc'>('asc');
	const [schoolSortBy, setSchoolSortBy] = useState<'maTruong' | 'tenTruong' | 'top149' | 'oldCodes' | string>('tenTruong');

	const [selectedSchool, setSelectedSchool] = useState<HighSchool | null>(null);
	const [schoolDialogOpen, setSchoolDialogOpen] = useState(false);
	const [schoolSearchValue, setSchoolSearchValue] = useState('');

	const { data: provinceData, isLoading: isLoadingSchools } = useQuery({
		queryKey: ['province', province?.maTinh, { includeSchools: true, year, top149Only, merged }],
		queryFn: async () => {
			if (!province?.maTinh) return null;
			return provinceApi.getProvinceById(province.maTinh, true, merged);
		},
		enabled: (mode === 'edit' || mode === 'view') && !!province?.maTinh
	});

	// Danh sách tỉnh để chọn tỉnh sau sáp nhập. Chỉ nạp ở chế độ sửa: chế độ xem
	// và tạo mới không dùng tới, nạp thừa chỉ làm dialog mở chậm.
	const { data: provinceOptionsData } = useQuery({
		queryKey: ['mergedProvinceCatalog'],
		queryFn: () => provinceApi.getMergedCatalog(),
		enabled: isEditMode,
		staleTime: 5 * 60 * 1000,
	});

	const { data: schoolStatsData, isLoading: isLoadingStats } = useQuery({
		queryKey: ['schoolStats', province?.maTinh, { merged, selectedFaculty }],
		queryFn: async () => {
			if (!province?.maTinh) return null;
			const params = {
				maTinh: merged ? undefined : province.maTinh,
				maTinhMoi: merged ? [province.maTinh] : undefined,
				limit: 1000,
				maKhoa: selectedFaculty ? [selectedFaculty] : undefined,
			};
			return schoolApi.getSchoolStats(params);
		},
		enabled: (mode === 'edit' || mode === 'view') && !!province?.maTinh
	});

	const allSchools = useMemo(() => provinceData?.data?.schools || [], [provinceData?.data?.schools]);
	const mergedProvinces = provinceData?.data?.mergedProvinces || [];

	const availableYears = useMemo(() => {
		const yearsSet = new Set<number>();
		if (schoolStatsData?.data?.years) {
			schoolStatsData.data.years.forEach(y => yearsSet.add(y));
		}
		return Array.from(yearsSet).sort((a, b) => b - a);
	}, [schoolStatsData]);

	// Calculate total students by year for statistics summary
	const totalStudentsByYear = useMemo(() => {
		const totals: Record<string, number> = {};
		if (schoolStatsData?.data?.stats) {
			schoolStatsData.data.stats.forEach(stat => {
				if (stat.yearData) {
					Object.entries(stat.yearData).forEach(([year, count]) => {
						totals[year] = (totals[year] || 0) + (count as number);
					});
				}
			});
		}
		return totals;
	}, [schoolStatsData]);

	const schoolsWithYearData: SchoolWithYearData[] = useMemo(() => {
		let filtered = [...allSchools];
		if (top149Only) {
			filtered = filtered.filter(school => school.top149);
		}

		const statsMap = new Map<string, Record<string, number>>();
		if (schoolStatsData?.data?.stats) {
			schoolStatsData.data.stats.forEach(stat => {
				statsMap.set(stat.maTruong, stat.yearData);
			});
		}

		return filtered.map(school => ({
			...school,
			yearData: statsMap.get(school.maTruong) || {},
		}));
	}, [allSchools, top149Only, schoolStatsData]);

	// Filter schools by search value (tenTruong or maTruong)
	const filteredSchools = useMemo(() => {
		if (!schoolSearchValue.trim()) return schoolsWithYearData;
		const searchLower = schoolSearchValue.toLowerCase().trim();
		return schoolsWithYearData.filter(school =>
			school.tenTruong.toLowerCase().includes(searchLower) ||
			school.maTruong.toLowerCase().includes(searchLower)
		);
	}, [schoolsWithYearData, schoolSearchValue]);

	const sortedSchools = useMemo(() => {
		const sorted = [...filteredSchools];
		sorted.sort((a, b) => {
			let comparison = 0;

			if (schoolSortBy === 'top149') {
				const aVal = a.top149 ? 1 : 0;
				const bVal = b.top149 ? 1 : 0;
				comparison = aVal - bVal;
			} else if (schoolSortBy === 'oldCodes') {
				const aVal = a.oldCodes?.length || 0;
				const bVal = b.oldCodes?.length || 0;
				comparison = aVal - bVal;
			} else if (schoolSortBy.match(/^\d{4}$/)) {
				const aVal = a.yearData?.[schoolSortBy] || 0;
				const bVal = b.yearData?.[schoolSortBy] || 0;
				comparison = aVal - bVal;
			} else {
				const aVal = (a as unknown as Record<string, unknown>)[schoolSortBy] as string || '';
				const bVal = (b as unknown as Record<string, unknown>)[schoolSortBy] as string || '';
				comparison = String(aVal).localeCompare(String(bVal));
			}

			return schoolSortOrder === 'asc' ? comparison : -comparison;
		});
		return sorted;
	}, [filteredSchools, schoolSortBy, schoolSortOrder]);

	const handleSchoolSort = (column: string) => {
		if (schoolSortBy === column) {
			setSchoolSortOrder(schoolSortOrder === 'asc' ? 'desc' : 'asc');
		} else {
			setSchoolSortBy(column);
			setSchoolSortOrder('asc');
		}
	};

	const handleSchoolRowClick = (school: SchoolWithYearData) => {
		const fullSchool: HighSchool = {
			maTruong: school.maTruong,
			tenTruong: school.tenTruong,
			top149: school.top149,
			maTinh: province?.maTinh || '',
			maNoiBo: null,
			ghiChu: null,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};
		setSelectedSchool(fullSchool);
		setSchoolDialogOpen(true);
	};

	const handleSchoolDialogClose = () => {
		setSchoolDialogOpen(false);
		setSelectedSchool(null);
	};

	// Giá trị liên kết lấy từ bản ghi chi tiết: danh sách ở lưới ngoài không mang
	// theo `maTinhMoi`, nên chỉ dựa vào prop `province` thì ô chọn sẽ trống dù
	// tỉnh đang có liên kết.
	const maTinhMoiHienTai = provinceData?.data?.maTinhMoi ?? province?.maTinhMoi ?? '';

	useEffect(() => {
		if ((mode === 'edit' || mode === 'view') && province) {
			setFormData({
				maTinh: province.maTinh || '',
				tenTinh: province.tenTinh || '',
				maTinhMoi: maTinhMoiHienTai || '',
			});
		} else {
			setFormData({
				maTinh: '',
				tenTinh: '',
				maTinhMoi: '',
			});
		}
	}, [province, mode, open, maTinhMoiHienTai]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isLoading) return;

		if (!formData.maTinh.trim() || !formData.tenTinh.trim()) {
			showError('Vui lòng điền đầy đủ thông tin');
			return;
		}

		if (mode === 'create') {
			handleCreate(formData);
		} else if (mode === 'edit' && province) {
			handleUpdate(province.maTinh, {
				tenTinh: formData.tenTinh,
				maTinhMoi: formData.maTinhMoi || null,
			});
		}
	};

	const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	// Giá trị hiện tại luôn phải nhìn thấy được, kể cả khi nó trỏ tới một mã không
	// còn là tỉnh nào — đó chính là trường hợp cần sửa nhất.
	const luaChonTinhSapNhap = useMemo(() => {
		const danhSach = (provinceOptionsData?.data || []).map((t) => ({
			maTinh: t.maTinh,
			nhan: `${t.maTinh} — ${t.tenTinh}`,
		}));

		if (maTinhMoiHienTai && !danhSach.some((t) => t.maTinh === maTinhMoiHienTai)) {
			danhSach.unshift({ maTinh: maTinhMoiHienTai, nhan: `${maTinhMoiHienTai} — (không khớp tỉnh nào)` });
		}

		return danhSach;
	}, [provinceOptionsData, maTinhMoiHienTai]);

	const isDataLoading = isLoadingSchools || isLoadingStats;

	const tableCellSx = isMobile ? { whiteSpace: 'normal', wordBreak: 'break-word' } : undefined;

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={DIALOG_SETTINGS.DETAIL.MAX_WIDTH}
				fullScreen={isMobile}
				fullWidth
				PaperProps={{
					sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
				}}
			>
				<form onSubmit={handleSubmit} style={isMobile ? { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' } : undefined}>
					<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
						{isMobile && (
							<IconButton
								aria-label="back"
								onClick={onClose}
								edge="start"
								disabled={isLoading}
								sx={{ mr: 1 }}
							>
								<ArrowBackIcon />
							</IconButton>
						)}
						{mode === 'create' ? 'Thêm tỉnh/thành phố mới' : mode === 'view' ? 'Thông tin tỉnh/thành phố' : 'Chỉnh sửa tỉnh/thành phố'}
					</DialogTitle>
					<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
							<TextField
								label="Mã tỉnh/thành phố"
								value={formData.maTinh}
								onChange={handleChange('maTinh')}
								disabled={isEditMode || isViewMode || isLoading}
								required={!isViewMode}
								fullWidth
								placeholder="VD: HN, HCM"
								InputProps={{ readOnly: isReadOnly }}
							/>
							<TextField
								label="Tên tỉnh/thành phố"
								value={formData.tenTinh}
								onChange={handleChange('tenTinh')}
								disabled={isViewMode || isLoading}
								required={!isViewMode}
								fullWidth
								placeholder="VD: Hà Nội, Thành phố Hồ Chí Minh"
								InputProps={{ readOnly: isReadOnly }}
							/>
							{/*
								Chỉ ở chế độ sửa: API tạo tỉnh không nhận liên kết sáp nhập, còn chế
								độ xem đã có bảng tỉnh thành viên bên dưới.

								Dùng select gốc của trình duyệt thay vì menu MUI: dialog chạy toàn
								màn hình trên mobile, nơi picker gốc thao tác dễ hơn hẳn với danh
								sách vài chục tỉnh.
							*/}
							{isEditMode && (
								<TextField
									id="province-merge-link"
									select
									SelectProps={{ native: true }}
									InputLabelProps={{ shrink: true }}
									label="Tỉnh sau sáp nhập"
									value={formData.maTinhMoi}
									onChange={handleChange('maTinhMoi')}
									disabled={isLoading}
									fullWidth
									helperText="Mã tỉnh còn lại sau sáp nhập. Để trống nếu tỉnh này không thuộc nhóm sáp nhập nào."
								>
									<option value="">Không sáp nhập</option>
									{luaChonTinhSapNhap.map((t) => (
										<option key={t.maTinh} value={t.maTinh}>
											{t.nhan}
										</option>
									))}
								</TextField>
							)}
							{/* Statistics Summary - shown for both merged and original */}
							{(isEditMode || isViewMode) && availableYears.length > 0 && (
								<StatisticsPanel
									yearData={totalStudentsByYear}
									isLoading={isDataLoading}
									totalLabel={`${schoolsWithYearData.length} trường`}
								/>
							)}
							{(isEditMode || isViewMode) && merged && mergedProvinces.length > 0 && (
								<>
									<Divider sx={{ my: 2 }} />
									<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
										Lịch sử sáp nhập ({mergedProvinces.length})
									</Typography>
									{isDataLoading ? (
										<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
											<CircularProgress size={24} />
										</Box>
									) : (
										<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell><strong>Mã Tỉnh Cũ</strong></TableCell>
														<TableCell><strong>Tên Tỉnh Cũ</strong></TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{mergedProvinces.map((mergedProvince) => (
														<TableRow key={mergedProvince.maTinh}>
															<TableCell sx={tableCellSx}>{mergedProvince.maTinh}</TableCell>
															<TableCell sx={tableCellSx}>{mergedProvince.tenTinh}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</>
							)}
							{(isEditMode || isViewMode) && schoolsWithYearData.length > 0 && (
								<>
									<Divider sx={{ my: 2 }} />
									<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
										Danh sách trường ({schoolsWithYearData.length})
										{top149Only && <Chip label="Chỉ Top 149" size="small" color="primary" sx={{ ml: 1 }} />}
										{year && <Chip label={`Năm ${year}`} size="small" color="secondary" sx={{ ml: 1 }} />}
										{merged && <Chip label="Sau sáp nhập" size="small" color="info" sx={{ ml: 1 }} />}
										{!merged && <Chip label="Trước sáp nhập" size="small" color="warning" sx={{ ml: 1 }} />}
									</Typography>
									<Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
										Click vào hàng để xem chi tiết trường
									</Typography>
									<TextField
										size="small"
										placeholder="Tìm theo tên hoặc mã trường..."
										value={schoolSearchValue}
										onChange={(e) => setSchoolSearchValue(e.target.value)}
										sx={{ mb: 1.5, maxWidth: { xs: '100%', sm: 300 } }}
										slotProps={{
											input: {
												startAdornment: (
													<InputAdornment position="start">
														<SearchIcon color="action" fontSize="small" />
													</InputAdornment>
												),
												endAdornment: schoolSearchValue && (
													<InputAdornment position="end">
														<IconButton
															size="small"
															onClick={() => setSchoolSearchValue('')}
															edge="end"
														>
															<ClearIcon fontSize="small" />
														</IconButton>
													</InputAdornment>
												),
											},
										}}
									/>
									{schoolSearchValue && (
										<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
											Tìm thấy {filteredSchools.length} / {schoolsWithYearData.length} trường
										</Typography>
									)}
									{isDataLoading ? (
										<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
											<CircularProgress size={24} />
										</Box>
									) : (
										<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell sx={{ minWidth: isMobile ? 70 : 100 }}>
															<TableSortLabel
																active={schoolSortBy === 'maTruong'}
																direction={schoolSortBy === 'maTruong' ? schoolSortOrder : 'asc'}
																onClick={() => handleSchoolSort('maTruong')}
															>
																<strong>Mã Trường</strong>
															</TableSortLabel>
														</TableCell>
														<TableCell sx={{ minWidth: isMobile ? 120 : 180 }}>
															<TableSortLabel
																active={schoolSortBy === 'tenTruong'}
																direction={schoolSortBy === 'tenTruong' ? schoolSortOrder : 'asc'}
																onClick={() => handleSchoolSort('tenTruong')}
															>
																<strong>Tên Trường</strong>
															</TableSortLabel>
														</TableCell>
														<TableCell align="center" sx={{ minWidth: 80 }}>
															<TableSortLabel
																active={schoolSortBy === 'top149'}
																direction={schoolSortBy === 'top149' ? schoolSortOrder : 'asc'}
																onClick={() => handleSchoolSort('top149')}
															>
																<strong>Top 149</strong>
															</TableSortLabel>
														</TableCell>
														{availableYears.map(yearCol => (
															<TableCell key={yearCol} align="right" sx={{ minWidth: 60 }}>
																<TableSortLabel
																	active={schoolSortBy === String(yearCol)}
																	direction={schoolSortBy === String(yearCol) ? schoolSortOrder : 'asc'}
																	onClick={() => handleSchoolSort(String(yearCol))}
																>
																	<strong>{yearCol}</strong>
																</TableSortLabel>
															</TableCell>
														))}
														{!isMobile && (
															<TableCell sx={{ minWidth: 150 }}>
																<TableSortLabel
																	active={schoolSortBy === 'oldCodes'}
																	direction={schoolSortBy === 'oldCodes' ? schoolSortOrder : 'asc'}
																	onClick={() => handleSchoolSort('oldCodes')}
																>
																	<strong>Mã Cũ</strong>
																</TableSortLabel>
															</TableCell>
														)}
													</TableRow>
												</TableHead>
												<TableBody>
													{sortedSchools.map((school) => (
														<TableRow
															key={school.maTruong}
															hover
															onClick={() => handleSchoolRowClick(school)}
															sx={{ cursor: 'pointer' }}
														>
															<TableCell sx={tableCellSx}>{school.maTruong}</TableCell>
															<TableCell sx={tableCellSx}>{school.tenTruong}</TableCell>
															<TableCell align="center">
																{school.top149 && (
																	<Chip label="Top 149" size="small" color="primary" />
																)}
															</TableCell>
															{availableYears.map(yearCol => (
																<TableCell key={yearCol} align="right">
																	{school.yearData?.[String(yearCol)] || 0}
																</TableCell>
															))}
															{!isMobile && (
																<TableCell>
																	{school.oldCodes && school.oldCodes.length > 0 ? (
																		<Box>
																			{school.oldCodes.map((oldCode, idx) => (
																				<Typography key={idx} variant="body2" color="primary">
																					{oldCode.maTruong} - {oldCode.tenTruong}
																				</Typography>
																			))}
																		</Box>
																	) : (
																		<Typography variant="body2" color="text.disabled">
																			Không có
																		</Typography>
																	)}
																</TableCell>
															)}
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</>
							)}
						</Box>
					</DialogContent>
					<DialogFormActions
						mode={mode}
						isLoading={isLoading}
						isReadOnly={isReadOnly}
						isMobile={isMobile}
						onClose={onClose}
					/>
				</form>
			</Dialog>

			{selectedSchool && (
				<SchoolDialog
					open={schoolDialogOpen}
					onClose={handleSchoolDialogClose}
					school={selectedSchool}
					mode="edit"
					disabled={true}
				/>
			)}
		</>
	);
}
